// 该模块接口用于easy内部
import { ref, Ref, computed, VNode } from 'vue';
import { default as axios, AxiosResponse } from 'axios';
import { isPromise } from '@vue/shared';
import { IdLabel, IdLabelList } from './types';
import { useShareData } from './SharedData';
import { getTransformData, reloadTransform } from './Transform';
import { computedAsyncEx } from './Common';
import PinyinMatch from 'pinyin-match';

import { toInteger, noop } from 'lodash-es';

export function useDomMoreWatcher(
  options: IntersectionObserverInit,
  doms: HTMLElement[],
  moreDom: HTMLElement,
  callback: () => void
) {
  function _callback(entries: IntersectionObserverEntry[]) {
    let hasInsertMore = false;
    entries.forEach((entry) => {
      const target = entry.target as HTMLElement;
      const parent = target.parentNode;
      if (entry.intersectionRatio < 1) {
        target.style.visibility = 'hidden';
        if (!hasInsertMore) {
          parent?.insertBefore(moreDom, target);
          moreDom.style.display = '';
          hasInsertMore = true;
        }
      } else {
        moreDom.style.display = 'none';
        target.style.visibility = 'visible';
      }
    });

    /*let hidden = entries.find(e =>  (e.target as HTMLElement).style.visibility  == 'hidden');
		if(hidden) {
			const target = hidden.target as HTMLElement;
			const parent = target.parentNode;
			parent?.insertBefore(moreDom, target);
			moreDom.style.display = '';
		} else {
			moreDom.style.display = 'none';
		}*/

    callback();
  }

  let observer: IntersectionObserver | null = new IntersectionObserver(
    _callback,
    options
  );
  for (const dom of doms) {
    observer.observe(dom);
  }

  return () => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  };
}

function fixIdLabelList(list: IdLabelList, rawId?: boolean): IdLabelList {
  if (!list) return [];

  const newList = list.map((e) => ({
    id: rawId ? e.id : toInteger(e.id),
    label: e.label,
    disabled: e.disabled
  }));

  // 将disabled的移到列表后面
  if (newList.find((e) => e.disabled)) {
    const enableList = newList.filter((e) => !e.disabled);
    const disabledList = newList.filter((e) => e.disabled);

    return [...enableList, ...disabledList];
  }

  return newList;
}

function checkRestResponse(resp: AxiosResponse) {
  if (resp.status < 200 || resp.status >= 300) {
    return false;
  }

  if (resp.data.status) {
    return false;
  }

  return true;
}

/**
 * axiso 读取kv list
 * @param url
 * @returns
 */
function axioFetchKVList(url: string, outerRef?: Ref<IdLabelList>) {
  const listRef = outerRef ?? ref([] as IdLabelList);

  // 请求数据
  axios
    .post(url, {})
    .then((resp) => {
      // 检查status
      if (!checkRestResponse(resp)) {
        return;
      }

      // 判断列里是否包含停用字段
      const columns = resp.data.columns;
      const containsDisabled = columns.length > 2 && columns[2] == 'disabled';

      const data = resp.data.data as any[][];

      const list = [] as IdLabel[];
      const disabledList = [] as IdLabel[];

      for (const row of data) {
        const id = row[0] as number;
        const label = row[1] as string;

        const disabled = containsDisabled ? !!row[2] : undefined;
        // 将停用的放在列表最后
        if (disabled) {
          disabledList.push({ id, label, disabled });
        } else {
          list.push({ id, label, disabled });
        }
      }

      list.push(...disabledList);

      listRef.value = list;
    })
    .catch((reason) => {
      console.error(reason);
    });

  return listRef;
}

/**
 * 拼音匹配
 * @param content
 * @param keyword
 */
export function pinyinMatch(content: string, keyword: string) {
  return !keyword || !!PinyinMatch.match(content, keyword);
}

export type PinyinFilterCallback = (
  abortController: AbortController,
  ...args: Ref<any>[]
) => Promise<IdLabelList>;

/**
 * 用于下拉框, 获取数据, 并提供拼音过滤
 * @param url 通过url提供数据
 * @param transform  通过变换提供数据
 * @param callback   通过回调函数提供数据
 * @param excludes
 */
export function pinyinFilterData(
  url?: string,
  transform?: string,
  callback?: PinyinFilterCallback,
  callbackParams?: string[],
  isForm?: boolean, // 是search元素,还是form元素
  excludes?: (number | string)[],
  rawId?: boolean // 保持id原始类型, 默认会转成number
) {
  let optionsData: Ref<IdLabelList>;
  let args: Ref<any>[] = [];
  let reload: () => void;

  // 是否提供了数据源
  let hasData = true;

  // 从变换中拿数据
  if (transform) {
    optionsData = getTransformData(transform);

    // 触发重新加载
    reload = () => reloadTransform(transform);
  }
  // 从callback拿数据
  else if (callback) {
    if (callbackParams && callbackParams.length) {
      const model = isForm
        ? useShareData('form', 'model')
        : useShareData('search', 'params');
      args = callbackParams!.map((name) => computed(() => model[name]));
    }

    const { data: _data, load: load } = computedAsyncEx(
      async (abortController: AbortController) => {
        const res = await callback(abortController, ...args);
        return fixIdLabelList(res, rawId);
      },
      []
    );

    optionsData = _data;
    // 触发重新加载
    reload = () => load(true);
  } else if (url) {
    optionsData = ref<IdLabelList>([]);
    // 从api拿数据
    axioFetchKVList(url, optionsData);

    // 触发重新加载
    reload = () => axioFetchKVList(url, optionsData);
  } else {
    optionsData = ref([] as IdLabelList);

    reload = noop;
    hasData = false;
  }

  // 过滤关键词
  const filterWord = ref('');
  const filteredOptionsData = computed(() => {
    let filterResult = optionsData.value;
    if (excludes) {
      filterResult = optionsData.value.filter((i) => !excludes.includes(i.id));
    }

    if (filterWord.value) {
      const word = filterWord.value;
      filterResult = filterResult.filter(
        (i) => !!PinyinMatch.match(i.label, word)
      );
    }

    return filterResult;
  });

  const filterMethod = (value: string) => {
    filterWord.value = value;
  };

  const clearFilter = (show: boolean) => {
    if (!show) {
      filterWord.value = '';
    }
  };

  return {
    optionsData: filteredOptionsData,
    filter: filterMethod,
    clearFilter,
    reload,
    hasData
  };
}

// 对onClick包装, 避免重入, 及防抖
export function wrapOnClick(onClick: any) {
  let running = false;
  let lastTime = 0;

  return (...args: any[]) => {
    const now = new Date().valueOf();
    // 避免重复运行
    if (running || now - lastTime < 300) {
      lastTime = now;
      return;
    }
    lastTime = now;

    let result;
    try {
      running = true;
      result = args.length == 1 ? onClick(args[0]) : onClick(...args);
    } catch (e) {
      running = false;
      throw e;
    }

    if (isPromise(result)) {
      result.catch(noop).finally(() => (running = false));
    } else {
      running = false;
    }

    return result;
  };
}

// 枚举所有vnode
export function fixVNodeClickEvent(el: VNode) {
  if (el.props && el.props.onClick) {
    el.props.onClick = wrapOnClick(el.props.onClick);
  }

  if (el.children instanceof Array) {
    for (const c of el.children as any) {
      if (c instanceof Object) {
        fixVNodeClickEvent(c);
      }
    }
  }

  return el;
}
